import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Zap, Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
    const { login, refresh } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (loading) return; // hard guard against double-submit
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/dashboard");
        } catch (err) {
            // Race condition: it can happen that the call errors transiently
            // but the auth cookie was already set on a previous successful
            // attempt. Re-check before flashing a misleading red error.
            try {
                const { data } = await api.get("/auth/me");
                if (data && data.email) {
                    if (refresh) await refresh();
                    navigate("/dashboard");
                    return;
                }
            } catch { /* still not authenticated */ }
            const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
            // On mobile the "Identifiants invalides" error is often caused by
            // autofill picking a stale password or the keyboard adding silent
            // whitespace/capitalization. Add a hint so the user knows what to check.
            const isMobile = typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "");
            const is401 = err.response?.status === 401;
            if (isMobile && is401) {
                setError(`${detail} — Astuce : tape 👁️ pour vérifier ton mot de passe, et assure-toi qu'il n'y a ni espace ni majuscule accidentelle. Si tu as utilisé l'auto-remplissage, efface tout et retape à la main.`);
            } else {
                setError(detail);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-ink text-white p-12 grid-bg">
                <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo-link">
                    <span className="w-7 h-7 bg-orange text-ink flex items-center justify-center">
                        <Zap size={16} strokeWidth={2.5} />
                    </span>
                    <span className="h-display text-lg font-semibold">Profawz</span>
                </Link>
                <div className="max-w-md">
                    <div className="label-eyebrow text-orange/80 mb-3">Plateforme d'alternance</div>
                    <h2 className="h-display text-4xl font-semibold leading-tight">
                        Le suivi de vos étudiants BTS, en un seul endroit.
                    </h2>
                    <p className="mt-4 text-white/60 text-sm leading-relaxed">
                        Connectez-vous pour accéder à votre espace, créer un nouveau bilan ou consulter votre historique.
                    </p>
                </div>
                <div className="text-xs font-mono text-white/40">© {new Date().getFullYear()} Profawz</div>
            </div>

            <div className="flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-12">
                <div className="max-w-md w-full mx-auto">
                    <Link to="/" className="inline-flex items-center gap-1 text-xs font-mono text-muted2 hover:text-ink mb-6 transition-colors" data-testid="login-back-home">
                        ← Retour à l'accueil
                    </Link>
                    <div className="label-eyebrow mb-2">Connexion</div>
                    <h1 className="h-display text-3xl sm:text-4xl font-semibold mb-2">Re-bonjour 👋</h1>
                    <p className="text-ink/60 text-sm mb-10">
                        Pas encore de compte ?{" "}
                        <Link to="/register" className="font-semibold text-orange hover:underline" data-testid="login-register-link">
                            Créer un compte
                        </Link>
                    </p>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="label-eyebrow mb-2 block">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="field-input"
                                placeholder="vous@etablissement.fr"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                autoComplete="email"
                                inputMode="email"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow mb-2 block flex items-center justify-between">
                                <span>Mot de passe</span>
                                <Link to="/forgot-password" className="text-[10px] font-medium text-orange hover:underline normal-case tracking-normal" data-testid="login-forgot-link">
                                    Mot de passe oublié ?
                                </Link>
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="field-input !pr-11"
                                    placeholder="••••••••"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck="false"
                                    autoComplete="current-password"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(s => !s)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted2 hover:text-ink p-1"
                                    tabIndex={-1}
                                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                    data-testid="login-password-toggle"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div className="text-sm text-destructive border border-destructive/20 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="login-error">
                                {error}
                            </div>
                        )}
                        <button type="submit" disabled={loading} className="btn-primary w-full !py-3" data-testid="login-submit-button">
                            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                            {loading ? "Connexion…" : "Se connecter"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
